import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Inbox, RefreshCcw, Trash2, Mail, Phone, Check, Archive,
  MailOpen, Loader2, Search,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  listRichieste, updateRichiestaStatus, deleteRichiesta,
} from '../../lib/api';

const STATUS_LABELS = {
  new: { label: 'Nuova', tone: 'bg-sky-100 text-sky-700' },
  read: { label: 'Letta', tone: 'bg-slate-200 text-slate-700' },
  replied: { label: 'Risposta', tone: 'bg-emerald-100 text-emerald-700' },
  archived: { label: 'Archiviata', tone: 'bg-amber-100 text-amber-700' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const StatCard = ({ label, value, tone = 'bg-white', small = false }) => (
  <div className={`${tone} border border-slate-200 rounded-xl p-4 shadow-sm`}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 font-bold text-slate-800 ${small ? 'text-lg' : 'text-2xl'}`}>{value}</div>
  </div>
);

const RichiesteTab = ({ onUnreadChange }) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listRichieste(statusFilter);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
      onUnreadChange?.(data.unread || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/admin/login', { replace: true });
      } else {
        toast.error('Impossibile caricare le richieste');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate, onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateRichiestaStatus(id, newStatus);
      toast.success('Stato aggiornato');
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
      load();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa richiesta?')) return;
    try {
      await deleteRichiesta(id);
      toast.success('Richiesta eliminata');
      if (selected?.id === id) setSelected(null);
      load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const openDetail = async (item) => {
    setSelected(item);
    if (item.status === 'new') {
      try {
        await updateRichiestaStatus(item.id, 'read');
        load();
      } catch { /* ignore */ }
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((r) =>
      [r.name, r.email, r.phone, r.message].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Totali" value={total} tone="bg-white" />
        <StatCard label="Nuove" value={unread} tone="bg-sky-50" />
        <StatCard label="Filtrate" value={filtered.length} tone="bg-white" />
        <StatCard
          label="Stato filtro"
          value={statusFilter === 'all' ? 'Tutti' : STATUS_LABELS[statusFilter]?.label || statusFilter}
          tone="bg-white" small
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, messaggio..."
            className="pl-9 bg-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
        >
          <option value="all">Tutti gli stati</option>
          <option value="new">Nuove</option>
          <option value="read">Lette</option>
          <option value="replied">Risposte</option>
          <option value="archived">Archiviate</option>
        </select>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
          Aggiorna
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Nome</th>
                  <th className="text-left px-4 py-3 font-medium">Contatti</th>
                  <th className="text-left px-4 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Stato</th>
                  <th className="text-right px-4 py-3 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Caricamento...
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-500">
                    Nessuna richiesta trovata.
                  </td></tr>
                )}
                {!loading && filtered.map((r) => {
                  const st = STATUS_LABELS[r.status] || STATUS_LABELS.new;
                  const isSel = selected?.id === r.id;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => openDetail(r)}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-sky-50/40 ${isSel ? 'bg-sky-50' : ''} ${r.status === 'new' ? 'font-medium' : ''}`}
                    >
                      <td className="px-4 py-3">{r.name}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</div>
                        {r.phone && <div className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3" /> {r.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${st.tone}`}>{st.label}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                          className="text-slate-400 hover:text-red-600 p-1"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 h-fit sticky top-4">
          {selected ? (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{selected.name}</h3>
                  <div className="text-xs text-slate-500">{formatDate(selected.created_at)}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_LABELS[selected.status]?.tone}`}>
                  {STATUS_LABELS[selected.status]?.label}
                </span>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-sky-600 hover:underline">
                  <Mail className="w-4 h-4" /> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-sky-600 hover:underline">
                    <Phone className="w-4 h-4" /> {selected.phone}
                  </a>
                )}
                <div className="text-xs text-slate-500">
                  Email a info@igienizzazionecanali.it: {selected.email_sent ? 'inviata ✓' : 'non inviata'}
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-sm whitespace-pre-wrap mb-4 max-h-64 overflow-auto">
                {selected.message}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" onClick={() => handleStatusChange(selected.id, 'read')}>
                  <MailOpen className="w-4 h-4 mr-1" /> Letta
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleStatusChange(selected.id, 'replied')}>
                  <Check className="w-4 h-4 mr-1" /> Risposta
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleStatusChange(selected.id, 'archived')}>
                  <Archive className="w-4 h-4 mr-1" /> Archivia
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(selected.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Elimina
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center text-slate-500 py-8">
              <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Seleziona una richiesta per vederne i dettagli.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RichiesteTab;
